import { Rectangle, Texture } from 'pixi.js'
import { LOAD } from '../load.config'
import { packShelves } from '../scene/atlasLayout'
import { releaseCanvases } from '../scene/atlas'
import { SHEETS } from './reveal.config'
import { clipFrames } from './revealPlan'

// The reveal beat's effect textures (item H), built for one preset when the
// beat is attached and uploaded to the GPU before any run:
//   effects atlas(es)  explosion flipbook frames, smoke frames, debris variants
//   haze map           the heat distortion's displacement map (its own texture:
//                      the filter samples the whole texture, edges neutral)
// Every frame is sized from its ON-SCREEN size at this phone's resolution,
// LOAD.textureScale x (item F), not from the source sheet. Atlases are
// trimmed to what they hold and at most LOAD.atlasMaxPx square.

const pngs = ctx => {
    const out = new Map()
    for (const k of ctx.keys()) { const m = ctx(k); out.set(k.replace('./', ''), m.default ?? m) }
    return out
}
// See scene/atlas.js: webpack folds a `typeof require.context` test to false.
const contextPngs = load => { try { return pngs(load()) } catch { return new Map() } }
const SPRITE_URLS = contextPngs(() => require.context('../sprites', false, /\.png$/))

const imageCache = new Map()
function loadImage(file) {
    const url = SPRITE_URLS.get(file)
    if (!url) return Promise.resolve(null)
    if (!imageCache.has(file)) {
        imageCache.set(file, new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve(img)
            img.onerror = () => reject(new Error(`could not load ${file}`))
            img.src = url
        }))
    }
    return imageCache.get(file)
}

// Device pixels of an effect texture whose on-screen size is sizeR hex radii.
export const effectPx = (sizeR, R, resolution) => Math.ceil(sizeR * R * resolution * LOAD.textureScale)

function drawSheetFrame(c, img, sheet, index, w, h) {
    const cell = sheet.first + index
    const sx = (cell % sheet.cols) * sheet.frameW, sy = Math.floor(cell / sheet.cols) * sheet.frameH
    c.drawImage(img, sx, sy, sheet.frameW, sheet.frameH, 0, 0, w, h)
}

// ── Placeholders (Canvas 2D) ─────────────────────────────────────────────────
function placeholderFire(c, w, h, k, n) {
    const p = n > 1 ? k / (n - 1) : 0
    const r = w * (0.18 + 0.3 * Math.sqrt(p))
    const g = c.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, r)
    g.addColorStop(0, `rgba(255,250,220,${1 - p * 0.5})`)
    g.addColorStop(0.45, `rgba(255,170,40,${0.95 - p * 0.4})`)
    g.addColorStop(1, 'rgba(120,30,0,0)')
    c.fillStyle = g
    c.fillRect(0, 0, w, h)
}
function placeholderPuff(c, w, h, k, n) {
    let seed = 11 + k * 7
    const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff)
    const shade = 70 + Math.round((k / Math.max(1, n - 1)) * 60)
    for (let i = 0; i < 9; i++) {
        const x = w * (0.3 + rnd() * 0.4), y = h * (0.3 + rnd() * 0.4), r = w * (0.16 + rnd() * 0.14)
        const g = c.createRadialGradient(x, y, 0, x, y, r)
        g.addColorStop(0, `rgba(${shade},${shade},${shade},0.55)`)
        g.addColorStop(1, `rgba(${shade},${shade},${shade},0)`)
        c.fillStyle = g
        c.fillRect(0, 0, w, h)
    }
}
function placeholderDebris(c, w, h, k) {
    let seed = 5 + k * 13
    const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff)
    const pts = 5 + (k % 3)
    c.beginPath()
    for (let i = 0; i < pts; i++) {
        const a = (i / pts) * Math.PI * 2, r = w * (0.28 + rnd() * 0.18)
        c.lineTo(w / 2 + Math.cos(a) * r, h / 2 + Math.sin(a) * r)
    }
    c.closePath()
    const g = c.createLinearGradient(0, 0, w, h)
    g.addColorStop(0, ['#7a6a58', '#6b5d4c', '#8a7660', '#5d5246'][k % 4])
    g.addColorStop(1, '#2c241d')
    c.fillStyle = g
    c.fill()
    c.lineWidth = w * 0.05
    c.strokeStyle = 'rgba(20,14,10,0.9)'
    c.stroke()
}
// Heat-haze displacement map: smooth noise in R/G around 0.5, fading to exactly
// neutral (no offset) at the edge circle, so nothing outside it moves.
function hazeMap(px) {
    const cv = document.createElement('canvas')
    cv.width = cv.height = px
    const c = cv.getContext('2d')
    const img = c.createImageData(px, px)
    const f = [7.1, 11.3, 5.3, 9.7], ph = [0.4, 2.1, 4.3, 1.7]
    for (let y = 0; y < px; y++) {
        for (let x = 0; x < px; x++) {
            const u = x / px, v = y / px
            const dx = u - 0.5, dy = v - 0.5
            const r = Math.sqrt(dx * dx + dy * dy) * 2
            const fall = r >= 1 ? 0 : (1 - r) * (1 - r) * (3 - 2 * (1 - r)) // smooth to 0 at the edge
            const nx = 0.5 * Math.sin(u * f[0] * Math.PI + ph[0]) * Math.sin(v * f[1] * Math.PI + ph[1]) + 0.5 * Math.sin((u + v) * f[2] * Math.PI + ph[2])
            const ny = 0.5 * Math.sin(u * f[1] * Math.PI + ph[3]) * Math.sin(v * f[0] * Math.PI + ph[0]) + 0.5 * Math.sin((u - v) * f[3] * Math.PI + ph[1])
            const o = (y * px + x) * 4
            img.data[o] = Math.round(128 + 127 * fall * Math.max(-1, Math.min(1, nx)))
            img.data[o + 1] = Math.round(128 + 127 * fall * Math.max(-1, Math.min(1, ny)))
            img.data[o + 2] = 128
            img.data[o + 3] = 255
        }
    }
    c.putImageData(img, 0, 0)
    return cv
}

// cfg: one reveal.config.js preset. R: hex radius (CSS px). resolution: device px per CSS px.
export async function buildEffectTextures(cfg, { R, resolution, renderer = null }) {
    const items = []   // { id, group, w, h, draw(c, w, h) }
    const notes = {}
    const add = (group, frames, sizeR, drawFrame) => {
        const px = effectPx(sizeR, R, resolution)
        frames.forEach((f, k) => items.push({ id: `${group}#${k}`, group, w: px, h: px, draw: (c, w, h) => drawFrame(c, w, h, f, k, frames.length) }))
        notes[group] = { frames: frames.length, framePx: px }
    }

    if (cfg.explosion.enabled) {
        const sheet = SHEETS[cfg.explosion.sheet]
        if (!sheet) throw new Error(`unknown flipbook sheet "${cfg.explosion.sheet}"`)
        const img = await loadImage(sheet.file)
        const frames = clipFrames(cfg.explosion)
        add('explosion', frames, cfg.explosion.sizeR, img
            ? (c, w, h, f) => drawSheetFrame(c, img, sheet, f, w, h)
            : (c, w, h, f, k, n) => placeholderFire(c, w, h, k, n))
        notes.explosion.source = img ? sheet.file : 'placeholder'
    }
    if (cfg.smoke.enabled) {
        const sheet = cfg.smoke.sheet ? SHEETS[cfg.smoke.sheet] : null
        const img = sheet ? await loadImage(sheet.file) : null
        const frames = img ? clipFrames(cfg.smoke) : [0, 1, 2, 3]
        add('smoke', frames, cfg.smoke.sizeR, img
            ? (c, w, h, f) => drawSheetFrame(c, img, sheet, f, w, h)
            : (c, w, h, f, k, n) => placeholderPuff(c, w, h, k, n))
        notes.smoke.source = img ? sheet.file : 'placeholder'
    }
    if (cfg.debris.enabled) {
        const img = cfg.debris.sprite ? await loadImage(cfg.debris.sprite) : null
        const n = cfg.debris.variants
        const frames = Array.from({ length: n }, (_, k) => k)
        // Drawn at the largest debris size; smaller pieces are scaled down.
        add('debris', frames, cfg.debris.sizeR[1], img
            ? (c, w, h, f) => { const fw = img.width / n; c.drawImage(img, f * fw, 0, fw, img.height, 0, 0, w, h) }
            : (c, w, h, f) => placeholderDebris(c, w, h, f))
        notes.debris.source = img ? cfg.debris.sprite : 'placeholder'
    }

    const textures = { explosion: [], smoke: [], debris: [], haze: null }
    const sources = []
    const atlases = []
    let bytes = 0
    if (items.length) {
        const { atlases: packed, slots } = packShelves(items.map(it => ({ id: it.id, w: it.w, h: it.h })), LOAD.atlasMaxPx)
        const used = packed.map(() => ({ w: 0, h: 0 }))
        for (const it of items) {
            const s = slots.get(it.id)
            used[s.atlas].w = Math.max(used[s.atlas].w, s.x + it.w)
            used[s.atlas].h = Math.max(used[s.atlas].h, s.y + it.h)
        }
        const canvases = used.map(u => { const cv = document.createElement('canvas'); cv.width = u.w; cv.height = u.h; return cv })
        for (const it of items) {
            const s = slots.get(it.id)
            const c = canvases[s.atlas].getContext('2d')
            c.save(); c.translate(s.x, s.y); c.beginPath(); c.rect(0, 0, it.w, it.h); c.clip()
            it.draw(c, it.w, it.h)
            c.restore()
        }
        const bases = canvases.map(cv => Texture.from(cv))
        for (const it of items) {
            const s = slots.get(it.id)
            textures[it.group].push(new Texture({ source: bases[s.atlas].source, frame: new Rectangle(s.x, s.y, it.w, it.h) }))
        }
        sources.push(...bases)
        used.forEach(u => { atlases.push(`${u.w}x${u.h}`); bytes += u.w * u.h * 4 })
    }
    if (cfg.distortion.enabled) {
        const px = cfg.distortion.mapPx
        textures.haze = Texture.from(hazeMap(px))
        sources.push(textures.haze)
        bytes += px * px * 4
        notes.haze = { framePx: px }
    }
    if (renderer?.texture?.initSource) {
        for (const t of sources) renderer.texture.initSource(t.source)
        releaseCanvases(sources)
    }

    return {
        ...textures,
        stats: { atlases, hazeMapPx: cfg.distortion.enabled ? cfg.distortion.mapPx : null, textureBytes: bytes, groups: notes },
        destroy() {
            for (const g of ['explosion', 'smoke', 'debris']) for (const t of textures[g]) t.destroy(false)
            for (const b of sources) b.destroy(true)
        },
    }
}
